import type { JSONContent } from '@tiptap/core'
import { BlogFormValues } from './blogSchemas'
import { ArticleFormValues } from 'Models'

/** What TipTap's getJSON() returns for an empty editor. Using it as the default avoids a spurious "change" when the editor mounts. */
export const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] }

export const EMPTY_FORM_VALUES: BlogFormValues = {
  title: '',
  excerpt: '',
  content: EMPTY_DOC,
  category: '',
  tags: [],
  coverImage: null,
  coverImageAltText: '',
  visibility: '',
}

/**
 * Narrows whatever React Hook Form hands back (`watch` / `useWatch` return
 * DeepPartial values and include the ignored `visibility` field) down to
 * exactly the article fields the autosave system tracks and saves.
 */
export const pickArticleValues = (raw: Partial<BlogFormValues>): ArticleFormValues => ({
  title: raw.title ?? '',
  excerpt: raw.excerpt ?? '',
  content: raw.content ?? EMPTY_DOC,
  category: raw.category ?? '',
  tags: (raw.tags ?? []).filter((tag): tag is string => typeof tag === 'string'),
  coverImage: raw.coverImage ?? null,
  coverImageAltText: raw.coverImageAltText ?? '',
  metaTitle: raw.metaTitle,
  metaDescription: raw.metaDescription,
  canonicalUrl: raw.canonicalUrl,
  socialImageUrl: raw.socialImageUrl,
})

/**
 * Key-order-independent fingerprint of the tracked fields, used by the
 * autosave hook to decide whether anything really changed.
 */
export const fingerprintArticleValues = (values: ArticleFormValues): string =>
  JSON.stringify([
    values.title,
    values.excerpt,
    values.category,
    values.tags,
    values.coverImage ?? null,
    values.coverImageAltText,
    values.content,
    values.metaTitle ?? null,
    values.metaDescription ?? null,
    values.canonicalUrl ?? null,
    values.socialImageUrl ?? null,
  ])
